import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ChartBarIcon,
  PlusCircleIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import { getDashboardStatsThunk } from "../../../store/thunks/jewelleryPlan/jewelleryThunks";
import { getPendingPaymentsThunk } from "../../../store/thunks/jewelleryPlan/paymentThunks";
import { getPendingCancellationsThunk } from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import { getAllPlansThunk } from "../../../store/thunks/jewelleryPlan/planThunks";
import DashboardWidgets from "./DashboardWidgets";
import QuickActions from "./QuickActions";
import RecentActivity from "./RecentActivity";
import PaymentAgingChart from "./PaymentAgingChart";
import PlanStatusBreakdown from "./PlanStatusBreakdown";
import { JEWELLERY_ROUTES } from "../../../constants/jewellery.constant";

const PlansDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [refreshing, setRefreshing] = useState(false);

  const { stats, loading: dashboardLoading } = useSelector(
    (state) => state.jewelleryPlan?.jewellery.dashboard
  );
  const { selectedBranch } = useSelector(
    (state) => state.jewelleryPlan?.jewellery
  );
  const {
    pendingPayments,
    pendingSummary,
    loading: paymentsLoading,
  } = useSelector((state) => state.jewelleryPlan?.payments);
  const { pendingCancellations, loading: cancellationsLoading } = useSelector(
    (state) => state.jewelleryPlan?.cancellation
  );
  const { plans } = useSelector((state) => state.jewelleryPlan?.plans);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedBranch, selectedPeriod]);

  const fetchDashboardData = async () => {
    const filters = {
      period: selectedPeriod,
      ...(selectedBranch && { branchId: selectedBranch }),
    };

    try {
      setRefreshing(true);
      await Promise.all([
        dispatch(getDashboardStatsThunk(filters)),
        dispatch(getPendingPaymentsThunk(filters)),
        dispatch(getPendingCancellationsThunk(filters)),
        dispatch(getAllPlansThunk({ ...filters, status: "ACTIVE" })),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  // Calculate additional metrics
  const totalPendingAmount = pendingSummary?.totalAmount || 0;
  const pendingCount = pendingSummary?.total || 0;
  const agingData = pendingSummary?.aging || {};
  const urgentPayments = (agingData["7d+"] || []).length;

  // Plan status breakdown
  const planStats = plans.reduce(
    (acc, plan) => {
      acc[plan.status] = (acc[plan.status] || 0) + 1;
      if (plan.pauseMetrics?.currentlyPaused) {
        acc.PAUSED = (acc.PAUSED || 0) + 1;
      }
      if (plan.status === "ACTIVE" && plan.consecutiveMissedCount >= 2) {
        acc.AT_RISK = (acc.AT_RISK || 0) + 1;
      }
      return acc;
    },
    { ACTIVE: 0, PAUSED: 0, MATURED: 0, DEFAULTED: 0, AT_RISK: 0 }
  );

  const quickActions = [
    {
      title: "New Plan",
      description: "Create a new savings plan",
      icon: PlusCircleIcon,
      color: "bg-green-500",
      action: () => navigate(JEWELLERY_ROUTES.CREATE_PLAN),
    },
    {
      title: "Record Payment",
      description: "Add customer payment",
      icon: CurrencyRupeeIcon,
      color: "bg-blue-500",
      action: () => navigate(JEWELLERY_ROUTES.PAYMENTS),
    },
    {
      title: "Verify Payments",
      description: `${pendingCount} pending`,
      badge: urgentPayments > 0 ? `${urgentPayments} urgent` : null,
      icon: CheckCircleIcon,
      color:
        urgentPayments > 0
          ? "bg-red-500"
          : pendingCount > 0
          ? "bg-yellow-500"
          : "bg-gray-400",
      action: () => navigate(JEWELLERY_ROUTES.PAYMENT_VERIFICATION),
    },
    {
      title: "View Reports",
      description: "Analytics & insights",
      icon: ChartBarIcon,
      color: "bg-purple-500",
      action: () => navigate(JEWELLERY_ROUTES.REPORTS),
    },
  ];

  const loading = dashboardLoading || paymentsLoading || cancellationsLoading;

  return (
    <div className="space-y-6 p-4 lg:p-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Jewellery Plans Dashboard
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Overview of your savings plans and collections
              {selectedBranch && (
                <span className="ml-2 font-medium text-gray-700">
                  • Branch: {selectedBranch}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm rounded-md"
            >
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* Main Widgets */}
      <DashboardWidgets
        stats={stats}
        pendingSummary={pendingSummary}
        loading={loading}
      />

      {/* Quick Actions */}
      <QuickActions actions={quickActions} />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PaymentAgingChart agingData={agingData} loading={paymentsLoading} />
        <PlanStatusBreakdown planStats={planStats} loading={dashboardLoading} />
      </div>

      {/* Activity & Notifications Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* <RecentActivity /> */}

        {/* Critical Pending Actions */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              Critical Actions
            </h2>
            {(urgentPayments > 0 || pendingCancellations.length > 0) && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Attention Required
              </span>
            )}
          </div>

          <div className="space-y-3">
            {/* Urgent Payments (7+ days pending) */}
            {urgentPayments > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Urgent Payment Verifications
                    </p>
                    <p className="text-xs text-red-600 font-medium">
                      {urgentPayments} payments pending for 7+ days
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(JEWELLERY_ROUTES.PAYMENT_VERIFICATION)
                  }
                  className="text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Review Now
                </button>
              </div>
            )}

            {/* Regular Pending Verifications */}
            {pendingCount > urgentPayments && (
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-yellow-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Payment Verifications
                    </p>
                    <p className="text-xs text-gray-500">
                      {pendingCount - urgentPayments} payments awaiting
                      verification
                      {totalPendingAmount > 0 && (
                        <span className="ml-2 font-medium">
                          • ₹{totalPendingAmount.toLocaleString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(JEWELLERY_ROUTES.PAYMENT_VERIFICATION)
                  }
                  className="text-sm font-medium text-yellow-600 hover:text-yellow-500"
                >
                  Review
                </button>
              </div>
            )}

            {/* Pending Cancellations */}
            {pendingCancellations.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center">
                  <XCircleIcon className="h-5 w-5 text-orange-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Cancellation Requests
                    </p>
                    <p className="text-xs text-gray-500">
                      {pendingCancellations.length} requests awaiting approval
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(JEWELLERY_ROUTES.PENDING_CANCELLATIONS)
                  }
                  className="text-sm font-medium text-orange-600 hover:text-orange-500"
                >
                  Review
                </button>
              </div>
            )}

            {/* Upcoming Maturities */}
            {stats.upcomingMaturities > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Upcoming Maturities
                    </p>
                    <p className="text-xs text-gray-500">
                      {stats.upcomingMaturities} plans maturing this month
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(JEWELLERY_ROUTES.MATURED_PLANS)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  View
                </button>
              </div>
            )}

            {/* At-Risk Plans */}
            {planStats.AT_RISK > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      At-Risk Plans
                    </p>
                    <p className="text-xs text-gray-500">
                      {planStats.AT_RISK} plans with 2+ missed payments
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(JEWELLERY_ROUTES.OVERDUES)}
                  className="text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Follow Up
                </button>
              </div>
            )}

            {/* Paused Plans */}
            {planStats.PAUSED > 0 && (
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-purple-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Paused Plans
                    </p>
                    <p className="text-xs text-gray-500">
                      {planStats.PAUSED} plans currently paused
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(JEWELLERY_ROUTES.PLANS + "?status=PAUSED")
                  }
                  className="text-sm font-medium text-purple-600 hover:text-purple-500"
                >
                  View
                </button>
              </div>
            )}

            {/* No Pending Actions */}
            {pendingCount === 0 &&
              pendingCancellations.length === 0 &&
              stats.upcomingMaturities === 0 &&
              planStats.AT_RISK === 0 && (
                <div className="flex items-center justify-center p-8 text-gray-500">
                  <div className="text-center">
                    <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-medium">All caught up!</p>
                    <p className="text-xs">No pending actions at the moment</p>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlansDashboard;
